import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { Property } from 'app/modules/projects/projects.types';
import { PropertyTenant, PropertyUnit, RentEscalation } from 'app/modules/projects/details/tenants/tenants.types';
import { PartialFormField } from 'app/shared/components/form-fields/form-field.types';
@Injectable({
  providedIn: 'root',
})
export class PropertyTenantsService {
  private _propertyUnits: BehaviorSubject<PropertyUnit[] | null>;
  private _propertyUnit: BehaviorSubject<PropertyUnit | null>;
  private _property: Property;
  private _accountId: string;
  private _projectId: string;

  constructor(private _httpClient: HttpClient) {
    this._propertyUnits = new BehaviorSubject(null);
    this._propertyUnit = new BehaviorSubject(null);
  }

  get propertyUnits$(): Observable<PropertyUnit[]> {
    return this._propertyUnits.asObservable();
  }

  get propertyUnit$(): Observable<PropertyUnit> {
    return this._propertyUnit.asObservable();
  }

  set accountId(val: string) {
    this._accountId = val;
  }

  set property(val: Property) {
    this._property = val;
  }

  get property() {
    return this._property;
  }

  set projectId(val: string) {
    this._projectId = val;
  }

  get projectId() {
    return this._projectId;
  }

  get propertyUnitValue() {
    return this._propertyUnit.value;
  }

  getPropertyUnits(sort = { sort: 'unit_number', order: 'asc' }): Observable<PropertyUnit[]> {
    return this._httpClient
      .get<any>(`properties/${this.property.id}/property_units?sort=${sort.sort}&order=${sort.order}`)
      .pipe(
        map((response: any) => {
          this._propertyUnits.next(response.property_units);
          return response.property_units;
        }),
      );
  }

  getPropertyUnitsByPropertyId(id, sort = { sort: 'unit_number', order: 'asc' }): Observable<any[]> {
    return this._httpClient.get<any>(`properties/${id}/property_units?sort=${sort.sort}&order=${sort.order}`).pipe(
      tap((response: any) => {
        this._propertyUnits.next(response.property_units);
      }),
    );
  }

  createPropertyUnit(propertyUnit: PropertyUnit): Observable<PropertyUnit> {
    return this.propertyUnits$.pipe(
      take(1),
      switchMap((propertyUnits) =>
        this._httpClient
          .post<any>(`properties/${this.property.id}/property_units`, {
            property_unit: propertyUnit,
          })
          .pipe(
            map((reponse) => {
              this._propertyUnits.next([reponse.property_unit, ...propertyUnits]);

              return reponse.property_unit;
            }),
          ),
      ),
    );
  }

  getPropertyUnitById(id: string): Observable<PropertyUnit> {
    return this.propertyUnits$.pipe(
      take(1),
      switchMap((propertyUnits) =>
        this._httpClient.get<any>(`properties/${this.property.id}/property_units/${id}`).pipe(
          tap((response) => {
            this._propertyUnit.next(response.property_unit);
            return response.property_unit;
          }),
        ),
      ),
    );
  }

  updatePropertyUnit(propertyUnit: PropertyUnit): Observable<PropertyUnit> {
    return this.propertyUnits$.pipe(
      take(1),
      switchMap((propertyUnits) =>
        this._httpClient
          .put<any>(`properties/${this.property.id}/property_units/${propertyUnit.id}`, {
            property_unit: propertyUnit,
          })
          .pipe(
            map((response) => {
              let updatedPropertyUnit = response.property_unit;

              // Find the index of the updated task
              const index = propertyUnits.findIndex((item) => item.id === updatedPropertyUnit.id);

              // Update the contact
              propertyUnits[index] = updatedPropertyUnit;

              // Update the contacts
              this._propertyUnits.next(propertyUnits);

              // Return the updated contact
              return updatedPropertyUnit;
            }),
            switchMap((updatedPropertyUnit) =>
              this.propertyUnit$.pipe(
                take(1),
                filter((item) => item && item.id === updatedPropertyUnit.id),
                tap(() => {
                  // Update the task if it's selected
                  this._propertyUnit.next(updatedPropertyUnit);

                  // Return the updated task
                  return updatedPropertyUnit;
                }),
              ),
            ),
          ),
      ),
    );
  }

  deletePropertyUnit(id: number): Observable<any> {
    return this.propertyUnits$.pipe(
      take(1),
      switchMap((propertyUnits) =>
        this._httpClient.delete<any>(`properties/${this.property.id}/property_units/${id}`).pipe(
          map((reponse) => {
            // Find the index of the deleted label within the labels
            const index = propertyUnits.findIndex((item) => item.id === id);

            // Delete the label
            propertyUnits.splice(index, 1);

            // Update the labels
            this._propertyUnits.next(propertyUnits);

            // Return the deleted status
            return reponse.property_unit;
          }),
        ),
      ),
    );
  }

  deletePropertyTenant(unit_id: number, id: number): Observable<any> {
    return this.propertyUnits$.pipe(
      take(1),
      switchMap((propertyUnits) =>
        this._httpClient
          .delete<any>(`properties/${this.property.id}/property_units/${unit_id}/property_tenants/${id}`)
          .pipe(
            map((response) => {
              const unitIndex = propertyUnits.findIndex((item) => item.id === unit_id);
              if (unitIndex !== -1) {
                let propertyUnit = { ...propertyUnits[unitIndex] };
                propertyUnit.property_tenants = propertyUnit.property_tenants.filter((item) => item.id !== id);
                propertyUnits[unitIndex] = propertyUnit;
                this._propertyUnits.next([...propertyUnits]);
                this._propertyUnit.next(propertyUnit);
              }
              return response.property_tenant;
            }),
            catchError((error) => {
              console.error('Error deleting property tenant: ', error);
              return throwError(() => error);
            }),
          ),
      ),
    );
  }

  createPropertyTenant(propertyUnitId: number, propertyTenant: PropertyTenant): Observable<PropertyTenant> {
    return this.propertyUnits$.pipe(
      take(1),
      switchMap((propertyUnits) =>
        this._httpClient
          .post<any>(`properties/${this.property.id}/property_units/${propertyUnitId}/property_tenants`, {
            property_tenant: propertyTenant,
          })
          .pipe(
            map((reponse) => {
              const index = propertyUnits.findIndex((item) => item.id === propertyUnitId);
              let propertyUnit = propertyUnits[index];
              if (!propertyUnit.property_tenants) {
                propertyUnit.property_tenants = [];
              }
              propertyUnit.property_tenants.push(reponse.property_tenant);
              this._propertyUnit.next(propertyUnit);
              // Return the new contact
              return reponse.property_tenant;
            }),
          ),
      ),
    );
  }

  updatePropertyTenant(
    propertyUnitId: number,
    propertyTenant: PropertyTenant | PartialFormField,
    select?: string,
  ): Observable<PropertyTenant> {
    return this.propertyUnits$.pipe(
      take(1),
      switchMap((propertyUnits) =>
        this._httpClient
          .put<any>(
            `properties/${this.property.id}/property_units/${propertyUnitId}/property_tenants/${propertyTenant.id}`,
            { property_tenant: propertyTenant },
          )
          .pipe(
            map((response) => {
              // Find the index of the updated task
              const index = propertyUnits.findIndex((item) => item.id === propertyUnitId);

              let propertyUnit = propertyUnits[index];
              propertyUnit.property_tenant = response.property_tenant;
              // Update the contact
              propertyUnits[index] = propertyUnit;

              if (!propertyUnit.property_tenants) {
                propertyUnit.property_tenants = [];
              }
              const tenantIndex = propertyUnit.property_tenants.findIndex(
                (item) => item.id === response.property_tenant.id,
              );
              propertyUnit.property_tenants[tenantIndex] = response.property_tenant;
              this._propertyUnit.next(propertyUnit);

              // Update the contacts
              this._propertyUnits.next(propertyUnits);

              return response.property_tenant;
            }),
          ),
      ),
    );
  }

  createPropertyLease(
    propertyId: number,
    propertyTenant: PropertyTenant | PartialFormField,
  ): Observable<PropertyTenant> {
    return this._httpClient.post<any>(`property_tenants`, {
      property_tenant: {
        ...propertyTenant,
        property_id: propertyId,
      },
    });
  }

  updatePropertyLease(
    propertyId: number,
    propertyTenant: PropertyTenant | PartialFormField,
  ): Observable<PropertyTenant> {
    return this._httpClient.put<any>(`property_tenants/${propertyTenant.id}`, {
      property_tenant: {
        ...propertyTenant,
        property_id: propertyId,
      },
    });
  }

  createFacilityReport(propertyUnitId: number, facilityReport: any): Observable<any> {
    return this.propertyUnits$.pipe(
      take(1),
      switchMap((propertyUnits) =>
        this._httpClient
          .post<any>(`properties/${this.property.id}/property_units/${propertyUnitId}/facility_reports`, {
            facility_report: facilityReport,
          })
          .pipe(
            map((reponse) => {
              const index = propertyUnits.findIndex((item) => item.id === propertyUnitId);
              let propertyUnit = propertyUnits[index];
              if (!propertyUnit.facility_reports) {
                propertyUnit.facility_reports = [];
              }
              propertyUnit.facility_reports = [...propertyUnit.facility_reports, reponse.facility_report];
              this._propertyUnit.next(propertyUnit);
              return reponse.facility_report;
            }),
          ),
      ),
    );
  }

  updateFacilityReport(
    propertyUnitId: number,
    facilityReport: any | PartialFormField,
    select?: string,
  ): Observable<any> {
    return this.propertyUnits$.pipe(
      take(1),
      switchMap((propertyUnits) =>
        this._httpClient
          .put<any>(
            `properties/${this.property.id}/property_units/${propertyUnitId}/facility_reports/${facilityReport.id}`,
            { facility_report: facilityReport },
          )
          .pipe(
            map((response) => {
              // Find the index of the updated task
              const index = propertyUnits.findIndex((item) => item.id === propertyUnitId);

              const propertyUnit = propertyUnits[index];
              if (!propertyUnit.facility_reports) {
                propertyUnit.facility_reports = [];
              }
              const reportIndex = propertyUnit.facility_reports.findIndex(
                (item) => item.id === response.facility_report.id,
              );
              propertyUnit.facility_reports[reportIndex] = response.facility_report;
              this._propertyUnit.next(propertyUnits[index]);
              this._propertyUnits.next(propertyUnits);

              return response.property_tenant;
            }),
          ),
      ),
    );
  }

  deleteFacilityReport(unit_id: number, id: number): Observable<any> {
    return this.propertyUnits$.pipe(
      take(1),
      switchMap((propertyUnits) =>
        this._httpClient
          .delete<any>(`properties/${this.property.id}/property_units/${unit_id}/facility_reports/${id}`)
          .pipe(
            map((reponse) => {
              const unit_index = propertyUnits.findIndex((item) => item.id === unit_id);
              let propertyUnit = propertyUnits[unit_index];

              const report_index = propertyUnit.facility_reports.findIndex((item) => item.id === id);
              propertyUnit.facility_reports.splice(report_index, 1);

              this._propertyUnit.next(propertyUnit);

              return reponse.facility_report;
            }),
          ),
      ),
    );
  }

  saveRentEscalation(
    propertyId: number,
    propertyUnitId: number,
    propertyTenantId: number,
    rentEscalation: RentEscalation,
  ): Observable<RentEscalation> {
    if (rentEscalation.id) {
      return this._httpClient
        .put<any>(
          `properties/${propertyId}/property_units/${propertyUnitId}/property_tenants/${propertyTenantId}/rent_escalations/${rentEscalation.id}`,
          { rent_escalation: rentEscalation },
        )
        .pipe(
          map((response) => {
            return response.rent_escalation;
          }),
        );
    } else {
      return this._httpClient
        .post<any>(
          `properties/${propertyId}/property_units/${propertyUnitId}/property_tenants/${propertyTenantId}/rent_escalations`,
          { rent_escalation: rentEscalation },
        )
        .pipe(
          map((response) => {
            return response.rent_escalation;
          }),
        );
    }
  }

  deleteRentEscalation(
    propertyId: number,
    propertyUnitId: number,
    propertyTenantId: number,
    rentEscalationId: number,
  ): Observable<any> {
    return this._httpClient
      .delete<any>(
        `properties/${propertyId}/property_units/${propertyUnitId}/property_tenants/${propertyTenantId}/rent_escalations/${rentEscalationId}`,
      )
      .pipe(
        map((response) => {
          return response.rent_escalation;
        }),
      );
  }

  getRentSchedules(propertyId: number, propertyUnitId: number, propertyTenantId: number): Observable<PropertyUnit[]> {
    return this._httpClient
      .get<any>(
        `properties/${propertyId}/property_units/${propertyUnitId}/property_tenants/${propertyTenantId}/rent_schedules`,
      )
      .pipe(
        map((response: any) => {
          return response.rent_schedules;
        }),
      );
  }

  importLeaseAbstract(propertyId: number, propertyUnitId: number, document: any): Observable<any> {
    const uploadData = new FormData();
    uploadData.append('file', document);

    return this._httpClient
      .post<any>(`properties/${propertyId}/property_units/${propertyUnitId}/import_lease_abstract`, uploadData)
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((response) => {
          //console.error('Error importing lease abstract:', error);
          console.log(response.error);
          return of(response.error);
        }),
      );
  }
}
